import { useQuery } from "@apollo/client";
import CopyAllOutlined from "@mui/icons-material/CopyAllOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Refresh from "@mui/icons-material/Refresh";
import Sync from "@mui/icons-material/Sync";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  GridLegacy as Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { StatusChip } from "components/StatusChip";
import CopyableTypography from "components/Typography/CopyableTypography";
import { isWadmRunSummary } from "encamp-shared/src/hmbp/wadmRunSummary";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { JobTask, StaffJobDialogQuery } from "generated-graphql/graphql";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

import { STAFF_JOB_DIALOG_QUERY } from "./api";
import { JobDownloadButton } from "./JobDownloadButton";
import { JobLogTable } from "./JobLogsTable";
import { JobStepsDoneTable } from "./JobStepsDoneTable";
import { RequeueJobConfirmDialog } from "./RequeueJobConfirmDialog";
import { canRequeue, getJobStatusChipColor } from "./utils";
import { WadmRunSummarySection } from "./WadmRunSummarySection";

export function JobDialog({
  jobId,
  onClose,
  canRequeueJobs = false,
}: {
  jobId: string;
  onClose: () => void;
  canRequeueJobs?: boolean;
}) {
  const alerts = useAlerts();
  const copyToClipboard = useCopyToClipboard();
  const { data, refetch, loading } = useQuery<StaffJobDialogQuery>(STAFF_JOB_DIALOG_QUERY, {
    variables: { id: jobId },
  });
  const [requeueOpen, setRequeueOpen] = useState(false);
  const showRequeue = canRequeueJobs && canRequeue(data?.job.status, data?.job.task);
  const parametersJson = useMemo(
    () => JSON.stringify(data?.job.parameters, null, 2) ?? "",
    [data?.job.parameters]
  );
  const fullJobRecordJson = useMemo(
    () =>
      JSON.stringify(
        Object.fromEntries(Object.entries(data?.job.record ?? {}).filter(([_, v]) => v != null)),
        null,
        2
      ) ?? "",
    [data?.job.record]
  );

  const copySectionJson = useCallback(
    async (label: "Parameters" | "Full Job Record", jsonText: string) => {
      try {
        await copyToClipboard(jsonText);
        alerts.info(`${label} copied to clipboard`);
      } catch {
        alerts.error(`Failed to copy ${label.toLowerCase()}`);
      }
    },
    [alerts, copyToClipboard]
  );

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="lg"
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <span>Job Details</span>
            <Box display="flex" alignItems="center">
              {showRequeue && (
                <Tooltip title="Re-queue this failed job (reset to queued and re-run)">
                  <IconButton
                    color="info"
                    aria-label="Re-queue job"
                    onClick={() => setRequeueOpen(true)}
                    size="small"
                  >
                    <Sync />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Refresh job details">
                <span>
                  <IconButton
                    color="default"
                    aria-label="Refresh job details"
                    onClick={() => refetch()}
                    disabled={loading}
                    size="small"
                  >
                    {loading ? <CircularProgress size={20} /> : <Refresh />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        }
        noPrimaryAction
      >
        <Grid container rowSpacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="medium">
              Job ID:
            </Typography>
            <CopyableTypography variant="body2" text={data?.job.id} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="medium">
              Status:
            </Typography>
            {data?.job.status && (
              <StatusChip label={data.job.status} color={getJobStatusChipColor(data.job.status)} />
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle1" fontWeight="medium">
              Tenant:
            </Typography>
            <Typography variant="body2">{data?.job.tenant?.name ?? ""}</Typography>
          </Grid>
          <Grid item xs={6}>
            {data?.job.facility ? (
              <>
                <Typography variant="subtitle1" fontWeight="medium">
                  Facility:
                </Typography>
                <Box display="flex" alignItems="center">
                  <Link
                    to={`/o/${data.job.tenant?.id}/facilities/${data.job.facility.id}`}
                    target="_blank"
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" sx={{ marginRight: 1 }}>
                      {data.job.facility.name}
                    </Typography>
                    <OpenInNew fontSize="small" color="primary" />
                  </Link>
                </Box>
              </>
            ) : null}
          </Grid>
          {data?.job.document ? (
            <Grid item xs={6}>
              <Typography variant="subtitle1" fontWeight="medium">
                Document:
              </Typography>
              <Box display="flex" alignItems="center">
                <Link
                  to={`/o/${data.job.tenant?.id}/documents/${data.job.document.id}`}
                  target="_blank"
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ marginRight: 1 }}>
                    {data.job.document.title}
                  </Typography>
                  <OpenInNew fontSize="small" color="primary" />
                </Link>
              </Box>
            </Grid>
          ) : null}
          {data?.job.communication ? (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="medium">
                Communication:
              </Typography>
              <Typography variant="body2">
                <Tooltip title="View in Mail Room">
                  <Link to={`/staff/mail-room/status/${data.job.communication.id}`} target="_blank">
                    {prettyPrintEnumValue(data.job.communication.status)}
                  </Link>
                </Tooltip>
              </Typography>
            </Grid>
          ) : null}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="medium">
              Created:
            </Typography>
            <Typography variant="body2"></Typography>
            {prettyPrintDateTime(data?.job.createdAt)}
            <Typography variant="body2">{data?.job.createdBy}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="medium">
              Updated:
            </Typography>
            <Typography variant="body2">{prettyPrintDateTime(data?.job.updatedAt)}</Typography>
            <Typography variant="body2">{data?.job.updatedBy}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Typography variant="subtitle1" fontWeight="medium">
                    Parameters
                  </Typography>
                  <Tooltip title="Copy Parameters JSON">
                    <IconButton
                      component="span"
                      role="button"
                      tabIndex={0}
                      size="small"
                      aria-label="Copy Parameters JSON"
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        void copySectionJson("Parameters", parametersJson);
                      }}
                    >
                      <CopyAllOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <pre>{parametersJson}</pre>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Typography variant="subtitle1" fontWeight="medium">
                    Full Job Record
                  </Typography>
                  <Tooltip title="Copy Full Job Record JSON">
                    <IconButton
                      component="span"
                      role="button"
                      tabIndex={0}
                      size="small"
                      aria-label="Copy Full Job Record JSON"
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        void copySectionJson("Full Job Record", fullJobRecordJson);
                      }}
                    >
                      <CopyAllOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  This is the full Job record from the database with null values removed:
                </Typography>
                <pre>{fullJobRecordJson}</pre>
              </AccordionDetails>
            </Accordion>

            {data?.job.output?.downloadKeys && data?.job.output?.downloadKeys.length > 0 && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <JobDownloadButton downloadKeys={data.job.output.downloadKeys} />
              </Grid>
            )}
          </Grid>

          {data?.job.task === JobTask.ProcessAmazonWadm && isWadmRunSummary(data.job.output) && (
            <Grid item xs={12}>
              <WadmRunSummarySection summary={data.job.output} />
            </Grid>
          )}

          <Grid item xs={12}>
            <JobLogTable jobId={data?.job.id ?? ""} />
          </Grid>
          <Grid item xs={12}>
            <JobStepsDoneTable jobId={data?.job.id ?? ""} />
          </Grid>
        </Grid>
      </Dialog>
      {requeueOpen && (
        <RequeueJobConfirmDialog
          open
          jobId={jobId}
          task={data?.job.task}
          status={data?.job.status}
          onClose={() => setRequeueOpen(false)}
          onRequeued={() => refetch()}
        />
      )}
    </>
  );
}
